'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { lookupCartPrices, type CartPriceLookup } from '@/lib/cart/actions';
import { useCart } from '@/lib/cart/store';
import { formatDealerPrice, formatMoney } from '@/lib/pricing/format';
import { AvailabilityBadge } from './AvailabilityBadge';

interface CartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CartDrawer({ open, onOpenChange }: CartDrawerProps) {
  const router = useRouter();
  const { items, setQty, remove, clear, totalQty } = useCart();
  const [prices, setPrices] = useState<CartPriceLookup[]>([]);
  const [loading, setLoading] = useState(false);

  const skus = Object.keys(items);

  // Fetch latest prices when the drawer opens or its contents change. The
  // setState calls inside this effect are syncing state with an external
  // system (Server Action + DB), which is the documented use case for
  // setState in an effect.
  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!open || skus.length === 0) {
      setPrices([]);
      return;
    }
    setLoading(true);
    let cancelled = false;
    lookupCartPrices(skus)
      .then((p) => {
        if (!cancelled) setPrices(p);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // skus joined into a stable string so the effect only fires when membership
    // changes, not when reference identity does.
  }, [open, skus.join('|')]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  const priceByLinkSku = new Map(prices.map((p) => [p.linkSku, p]));
  const subtotal = skus.reduce((sum, sku) => {
    const line = items[sku];
    const p = priceByLinkSku.get(sku);
    if (!line || !p?.price) return sum;
    return sum + line.qty * p.price;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-t-4 border-amber-500">
        <DialogHeader>
          <DialogTitle className="text-xl">Your cart</DialogTitle>
          <DialogDescription>
            {totalQty === 0
              ? 'Your cart is empty.'
              : `${totalQty} item${totalQty === 1 ? '' : 's'} ready to submit.`}
          </DialogDescription>
        </DialogHeader>

        {skus.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-500">
            Add items from the catalog to get started.
          </p>
        ) : (
          <ul className="max-h-[50vh] divide-y divide-neutral-200 overflow-y-auto">
            {skus.map((sku) => {
              const line = items[sku]!;
              const p = priceByLinkSku.get(sku);
              return (
                <li key={sku} className="flex items-start gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-semibold">{sku}</p>
                    <p className="mt-0.5 text-xs text-neutral-600">
                      {loading ? (
                        <span className="text-neutral-400">Looking up price…</span>
                      ) : p ? (
                        <>
                          <span className="font-medium tabular-nums">
                            {formatDealerPrice(p.price)}
                          </span>{' '}
                          ea
                          <AvailabilityBadge availability={p.availability} />
                        </>
                      ) : (
                        <span className="text-neutral-400">Price unavailable</span>
                      )}
                    </p>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    value={line.qty}
                    onChange={(e) => setQty(sku, Number.parseInt(e.target.value || '0', 10))}
                    className="w-16"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(sku)}
                    aria-label={`Remove ${sku}`}
                  >
                    ×
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        {skus.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-bold tracking-widest text-amber-700 uppercase">
                Subtotal
              </span>
              <span className="text-xl font-bold text-neutral-900 tabular-nums">
                {formatMoney(subtotal)}
              </span>
            </div>
            <p className="mt-1 text-xs text-neutral-600">
              Final price re-confirmed at submit. Tax + shipping applied at checkout.
            </p>
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={clear} disabled={skus.length === 0}>
            Clear cart
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Keep shopping
            </Button>
            <Button
              type="button"
              disabled={skus.length === 0}
              onClick={() => {
                onOpenChange(false);
                router.push('/checkout');
              }}
            >
              Checkout
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
